/**
 * Карта навигации Рублокса — единый источник правды.
 *
 * Раньше список вкладок был продублирован в ЧЕТЫРЁХ местах (CabinetPage,
 * CabinetChrome, WalletPage, UserProfilePage), причём двумя разными
 * наборами. Из-за этого «Стена» и «Магазин» были недостижимы из самого
 * кабинета — попасть на них можно было только через страницу Идей.
 *
 * Теперь навигация — ДАННЫЕ, а не JSX. Компоненты рендерят её по-своему
 * (сайдбар, таб-бар, футер), но структура одна.
 *
 * ── Переключатель контекста ──────────────────────────────────────────
 * У игрока и автора игр разные задачи, поэтому и наборы пунктов разные.
 * Сейчас оба видят одно меню, где половина пунктов лишняя каждому.
 * Переключатель живёт НАД меню и подменяет группу «Играть»/«Создавать».
 */

/** Режимы: что человек сейчас делает на платформе. */
export const MODES = [
  { id: "play", label: "Играю", icon: "gamepad" },
  { id: "create", label: "Создаю", icon: "puzzle" },
];

export const DEFAULT_MODE = "play";
const MODE_STORAGE_KEY = "rublox_nav_mode";

export function getStoredMode() {
  try {
    const m = localStorage.getItem(MODE_STORAGE_KEY);
    return MODES.some((x) => x.id === m) ? m : DEFAULT_MODE;
  } catch {
    return DEFAULT_MODE;
  }
}

export function storeMode(mode) {
  try {
    localStorage.setItem(
      MODE_STORAGE_KEY,
      MODES.some((x) => x.id === mode) ? mode : DEFAULT_MODE
    );
  } catch {
    /* приватный режим */
  }
}

/**
 * Пункт навигации.
 *   to    — путь (или ключ внешнего продукта при eco)
 *   auth  — только для вошедших. Гостю НЕ показываем: вести его в
 *           заглушку «нужно войти» значит обещать то, чего он не увидит.
 *           Витрина (каталог, топ, профили) остаётся открытой всем.
 *   eco   — внешний продукт экосистемы, открывается через SSO-тикет,
 *           а не роутером.
 *   end   — точное совпадение пути для подсветки (нужно там, где есть
 *           подмаршруты: /app и /app/games).
 */

/** Группы, зависящие от режима. */
const MODE_GROUPS = {
  play: {
    label: "Играть",
    items: [
      { to: "/app", label: "Главная", icon: "home", end: true },
      { to: "/app/games", label: "Каталог", icon: "gamepad" },
      { to: "/app/top", label: "Топ", icon: "trophy" },
    ],
  },
  create: {
    label: "Создавать",
    items: [
      { to: "/app/my", label: "Мои игры", icon: "puzzle", auth: true },
      { to: "studio", label: "Студия", icon: "rocket", eco: "studio" },
      { to: "/app/wiki", label: "Как создавать", icon: "book" },
    ],
  },
};

/** Группы, общие для обоих режимов. */
const COMMON_GROUPS = [
  {
    label: "Сообщество",
    items: [
      { to: "/app/wall", label: "Стена", icon: "chat" },
      { to: "/app/friends", label: "Друзья", icon: "users", auth: true },
    ],
  },
  {
    label: "Профиль",
    items: [
      /* «Профиль», а не «Кабинет»: кабинетом называется вся внутренняя
         часть сайта (/app), и пункт с тем же именем внутри неё сбивал
         с толку — человек не понимал, куда именно попадёт. */
      { to: "/app/me", label: "Профиль", icon: "userCircle", auth: true },
      { to: "/app/shop", label: "Магазин", icon: "palette" },
      { to: "/app/wallet", label: "Кошелёк", icon: "diamond", auth: true },
    ],
  },
];

/**
 * Навигация для режима и состояния авторизации.
 * Пустые группы (все пункты требуют входа) выбрасываем — заголовок без
 * пунктов выглядит как ошибка.
 */
export function navFor(mode = DEFAULT_MODE, isAuthenticated = false) {
  const modeGroup = MODE_GROUPS[mode] || MODE_GROUPS[DEFAULT_MODE];
  const groups = [modeGroup, ...COMMON_GROUPS];

  return groups
    .map((g) => ({
      ...g,
      items: g.items.filter((i) => !i.auth || isAuthenticated),
    }))
    .filter((g) => g.items.length > 0);
}

/**
 * Плоский список пунктов — для мобильного таб-бара, где группы не нужны.
 * Берём главные: не больше пяти, иначе подписи не помещаются.
 */
export function tabBarFor(mode = DEFAULT_MODE, isAuthenticated = false) {
  const flat = navFor(mode, isAuthenticated).flatMap((g) => g.items);
  return flat.filter((i) => !i.eco).slice(0, 5);
}

/**
 * Справочные ссылки — в футер, а не в меню. В школе так же: справочное
 * не должно занимать место рядом с рабочими разделами.
 */
export const FOOTER_LINKS = [
  { to: "/app/news", label: "Блог" },
  { to: "/ideas", label: "Идеи" },
  { to: "/support", label: "Поддержка" },
  { to: "/download", label: "Скачать" },
  { to: "/app/about", label: "О Рублоксе" },
  { to: "/terms", label: "Оферта" },
  { to: "/privacy-policy", label: "Конфиденциальность" },
];
